import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PostVideoVersionAudiotracksResult,
  PostVideoVersionAudiotracksOptions,
  GetVideoVersionAudiotracksOptions,
  GetVideoVersionAudiotracksResult,
  getVideoVersionAudiotracks,
  PostVideoVersionAudiotracksFields,
  GetVideoVersionAudiotracksFields,
  postVideoVersionAudiotracks,
} from '../fetchers/video/version/audiotracks';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetVideoVersionAudiotracksOptions<Fields extends GetVideoVersionAudiotracksFields> =
  GetVideoVersionAudiotracksOptions<Fields>;

/**
 * Get all audio tracks for a video version
 * This method returns all audio tracks that are associated with the specified video version.
 */

export function useGetVideoVersionAudiotracks<Fields extends GetVideoVersionAudiotracksFields>(
  getOptions:
    | UseGetVideoVersionAudiotracksOptions<Fields>
    | (() => UseGetVideoVersionAudiotracksOptions<Fields> | null),
  config?: SWRConfiguration<GetVideoVersionAudiotracksResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options
      ? `/videos/${options.where.videoId}/versions/${
          options.where.versionId
        }/audiotracks${serializeQuery(options)}`
      : () => null,
    options
      ? () => {
          return getVideoVersionAudiotracks({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoVersionAudiotracks, {
    endpoint: '/videos/:videoId/versions/:versionId/audiotracks',
    method: 'GET',
  });
}

/**
 * Get all audio tracks for a video version
 * This method returns all audio tracks that are associated with the specified video version.
 */

export function useGetVideoVersionAudiotracksLazy<
  Fields extends GetVideoVersionAudiotracksFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetVideoVersionAudiotracksResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetVideoVersionAudiotracksOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/versions/${
            options.where.versionId
          }/audiotracks${serializeQuery(options)}`,
          getVideoVersionAudiotracks({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoVersionAudiotracksLazy, {
    endpoint: '/videos/:videoId/versions/:versionId/audiotracks',
    method: 'GET',
  });
}

type UsePostVideoVersionAudiotracksOptions<Fields extends PostVideoVersionAudiotracksFields> =
  PostVideoVersionAudiotracksOptions<Fields>;

/**
 * Create an audio track for a video version
 * This method creates a new audio track for a video version.
 */

export function usePostVideoVersionAudiotracks<Fields extends PostVideoVersionAudiotracksFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PostVideoVersionAudiotracksResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePostVideoVersionAudiotracksOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/versions/${
            options.where.versionId
          }/audiotracks${serializeQuery(options)}`,
          postVideoVersionAudiotracks({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePostVideoVersionAudiotracks, {
    endpoint: '/videos/:videoId/versions/:versionId/audiotracks',
    method: 'POST',
  });
}
